import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import HeroSection from "@/components/HeroSection";
import PartnersStrip from "@/components/PartnersStrip";
import ReportDetails from "@/components/ReportDetails";
import HowItWorks from "@/components/HowItWorks";
import ProductPreview from "@/components/ProductPreview";
import TrustSection from "@/components/TrustSection";
import SupportedLabs from "@/components/SupportedLabs";
import WhatsIncluded from "@/components/WhatsIncluded";
import FaqSection from "@/components/FaqSection";
import KnowledgeCenter from "@/components/KnowledgeCenter";
import CtaBanner from "@/components/CtaBanner";
import AnalyzingState from "@/components/AnalyzingState";
import EmailCapture from "@/components/EmailCapture";
import SavedAnalysisBanner from "@/components/SavedAnalysisBanner";
import ChatLauncher from "@/components/chat/ChatLauncher";
import ChatDrawer from "@/components/chat/ChatDrawer";
import useScrollToHash from "@/hooks/useScrollToHash";
import { parsePdfFile, ParseApiError } from "@/lib/parsePdfApi";
import { MOCK_RESPONSE } from "@/lib/mockData";
import {
  saveAnalysis,
  loadAnalysis,
} from "@/lib/persistence";

export default function Home() {
  const navigate = useNavigate();
  useScrollToHash();

  const [parsing, setParsing] = useState(false);
  const [errorCode, setErrorCode] = useState(null);
  const [savedAnalysis, setSavedAnalysis] = useState(null);

  useEffect(() => {
    const saved = loadAnalysis();
    if (saved && saved.result) setSavedAnalysis(saved);
  }, []);

  const goToResult = useCallback(() => {
    navigate("/result");
  }, [navigate]);

  const startParse = useCallback(async (file) => {
    setErrorCode(null);
    setParsing(true);
    try {
      const data = await parsePdfFile(file);
      await new Promise((r) => setTimeout(r, 1800));
      saveAnalysis(data);
      setSavedAnalysis(null);
      goToResult();
    } catch (err) {
      const code = err instanceof ParseApiError ? err.code : "network";
      setErrorCode(code);
      setParsing(false);
    }
  }, [goToResult]);

  const startDemo = useCallback(async () => {
    setErrorCode(null);
    setParsing(true);
    await new Promise((r) => setTimeout(r, 2600));
    saveAnalysis(MOCK_RESPONSE);
    setSavedAnalysis(null);
    goToResult();
  }, [goToResult]);

  const resumeSaved = useCallback(() => {
    if (!savedAnalysis) return;
    setSavedAnalysis(null);
    goToResult();
  }, [savedAnalysis, goToResult]);

  const focusHeroUpload = useCallback(() => {
    const el = document.getElementById("hero-upload");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const showSavedBanner = savedAnalysis && savedAnalysis.result;

  return (
    <div className="App relative min-h-screen">
      <Header onUpload={focusHeroUpload} />

      <HeroSection
        uploadState={parsing ? "parsing" : errorCode ? "error" : "idle"}
        uploadError={errorCode}
        onFile={startParse}
        onDemo={startDemo}
        onRetry={() => setErrorCode(null)}
        savedBanner={
          showSavedBanner ? (
            <SavedAnalysisBanner
              savedAt={savedAnalysis.savedAt}
              fileName={savedAnalysis.result.originalFileName}
              onResume={resumeSaved}
            />
          ) : null
        }
      />

      {/*<PartnersStrip />*/}
      <ReportDetails onUpload={focusHeroUpload} />
      <HowItWorks />
      {/*<ProductPreview />*/}
      <TrustSection />
      <SupportedLabs />
      <WhatsIncluded onUpload={focusHeroUpload} />
      <KnowledgeCenter />
      <EmailCapture />
      <FaqSection />
      <CtaBanner onUpload={focusHeroUpload} />

      <AnalyzingState active={parsing} />

      <ChatLauncher />
      <ChatDrawer />

      <Footer />
    </div>
  );
}
